/**
 * PromptCostPanel
 *
 * Expandable inline panel showing all models for a specific prompt,
 * sorted by cost (cheapest first). Shows actual cost for completed runs
 * and estimated cost for models that haven't been run yet.
 */

import React, {useMemo} from 'react';
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import type {GridCellJob} from './PromptModelGrid';
import {formatCost} from '../../utils/format';

interface ReportSummary {
  id: string;
  promptName: string;
  modelName?: string | null;
  weightedScore?: number | null;
  passed: number;
  totalCases: number;
  totalCost?: number | null;
  inputTokens?: number | null;
  outputTokens?: number | null;
  ranAt: string;
}

interface ModelPricing {
  inputPer1M: number;
  outputPer1M: number;
  name?: string;
}

interface ModelRow {
  modelId: string;
  modelName: string;
  score: number | null;
  cost: number | null;
  estimated: boolean;
  reportId: string | null;
  running: boolean;
  jobId: string | null;
}

interface PromptCostPanelProps {
  promptName: string;
  promptDisplayName: string;
  reports: ReportSummary[];
  allModelIds: string[];
  modelPricing: Record<string, ModelPricing>;
  runningJobs: GridCellJob[];
  onClose: () => void;
  onReportClick: (reportId: string) => void;
  onRunEval: (promptName: string, modelName: string) => void;
  onJobClick: (jobId: string) => void;
}


export default function PromptCostPanel({
  promptName,
  promptDisplayName,
  reports,
  allModelIds,
  modelPricing,
  runningJobs,
  onClose,
  onReportClick,
  onRunEval,
  onJobClick,
}: PromptCostPanelProps) {
  const rows = useMemo(() => {
    // Get reports for this prompt
    const promptReports = reports.filter(r => r.promptName === promptName && r.modelName);

    // Build map: modelName -> latest report
    const reportByModel = new Map<string, ReportSummary>();
    for (const r of promptReports) {
      const existing = reportByModel.get(r.modelName!);
      if (!existing || r.ranAt > existing.ranAt) {
        reportByModel.set(r.modelName!, r);
      }
    }

    // Calculate average tokens from completed runs
    const completedReports = promptReports.filter(r => r.inputTokens && r.inputTokens > 0);
    const avgInputTokens = completedReports.length > 0
      ? completedReports.reduce((sum, r) => sum + (r.inputTokens || 0), 0) / completedReports.length
      : 0;
    const avgOutputTokens = completedReports.length > 0
      ? completedReports.reduce((sum, r) => sum + (r.outputTokens || 0), 0) / completedReports.length
      : 0;

    // Running jobs for this prompt
    const runningByModel = new Map<string, string>();
    for (const j of runningJobs) {
      if (j.promptName === promptName) runningByModel.set(j.modelName, j.jobId);
    }

    // Build all model rows — combine ran + unran
    const allModels = new Set(allModelIds);
    Array.from(reportByModel.keys()).forEach(k => allModels.add(k));

    const result: ModelRow[] = [];
    Array.from(allModels).forEach(modelId => {
      const report = reportByModel.get(modelId);
      const pricing = modelPricing[modelId];
      const displayName = pricing?.name || modelId.split('/').pop() || modelId;
      const jobId = runningByModel.get(modelId) || null;

      if (report) {
        result.push({
          modelId,
          modelName: displayName,
          score: report.weightedScore ?? null,
          cost: report.totalCost ?? null,
          estimated: false,
          reportId: report.id,
          running: !!jobId,
          jobId,
        });
      } else {
        // Estimate cost from average tokens + model pricing
        let estimatedCost: number | null = null;
        if (pricing && avgInputTokens > 0) {
          estimatedCost =
            (avgInputTokens / 1_000_000) * pricing.inputPer1M +
            (avgOutputTokens / 1_000_000) * pricing.outputPer1M;
        }
        result.push({
          modelId,
          modelName: displayName,
          score: null,
          cost: estimatedCost,
          estimated: true,
          reportId: null,
          running: !!jobId,
          jobId,
        });
      }
    });

    // Sort by cost ascending (nulls last)
    result.sort((a, b) => {
      if (a.cost == null && b.cost == null) return 0;
      if (a.cost == null) return 1;
      if (b.cost == null) return -1;
      return a.cost - b.cost;
    });

    return {rows: result, avgInputTokens, avgOutputTokens, completedCount: completedReports.length};
  }, [reports, promptName, allModelIds, modelPricing, runningJobs]);

  return (
    <Box sx={{
      bgcolor: '#fff',
      border: '1px solid #E5E7EB',
      borderRadius: '12px',
      overflow: 'hidden',
      mb: 2,
    }}>
      {/* Header */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 2,
        py: 1.5,
        borderBottom: '1px solid #F3F4F6',
        bgcolor: '#F9FAFB',
      }}>
        <Box>
          <Typography sx={{fontSize: 14, fontWeight: 600, color: '#111827'}}>
            {promptDisplayName}
          </Typography>
          <Typography sx={{fontSize: 11, color: '#9CA3AF'}}>
            {rows.completedCount > 0
              ? `Avg tokens: ${Math.round(rows.avgInputTokens).toLocaleString()} in / ${Math.round(rows.avgOutputTokens).toLocaleString()} out (from ${rows.completedCount} run${rows.completedCount !== 1 ? 's' : ''})`
              : 'No completed runs yet — estimates unavailable'}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{color: '#9CA3AF'}}>
          <CloseIcon sx={{fontSize: 16}} />
        </IconButton>
      </Box>

      {/* Rows */}
      {rows.rows.map((row, i) => (
        <Box
          key={row.modelId}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            px: 2,
            py: 1,
            borderBottom: i < rows.rows.length - 1 ? '1px solid #F3F4F6' : 'none',
            '&:hover': {bgcolor: '#FAFAFA'},
          }}
        >
          {/* Model name */}
          <Typography sx={{
            flex: 1,
            fontSize: 13,
            fontWeight: 500,
            color: row.estimated ? '#9CA3AF' : '#111827',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0,
          }}>
            {row.modelName}
          </Typography>

          {/* Score */}
          <Box sx={{width: 56, textAlign: 'center'}}>
            {row.score != null ? (
              <Chip
                label={`${row.score}%`}
                size="small"
                onClick={row.reportId ? () => onReportClick(row.reportId!) : undefined}
                sx={{
                  height: 20,
                  fontSize: 11,
                  fontWeight: 700,
                  cursor: row.reportId ? 'pointer' : 'default',
                  bgcolor: row.score >= 90 ? '#D1FAE5' : row.score >= 70 ? '#FEF3C7' : row.score >= 50 ? '#FED7AA' : '#FEE2E2',
                  color: row.score >= 90 ? '#065F46' : row.score >= 70 ? '#92400E' : row.score >= 50 ? '#9A3412' : '#991B1B',
                }}
              />
            ) : (
              <Typography sx={{fontSize: 11, color: '#D1D5DB'}}>---</Typography>
            )}
          </Box>

          {/* Cost */}
          <Box sx={{width: 80, textAlign: 'right'}}>
            {row.cost != null ? (
              <Typography sx={{
                fontSize: 12,
                fontWeight: row.estimated ? 400 : 600,
                color: row.estimated ? '#9CA3AF' : '#111827',
                fontStyle: row.estimated ? 'italic' : 'normal',
                fontVariantNumeric: 'tabular-nums',
              }}>
                {row.estimated ? '~' : ''}{formatCost(row.cost)}
              </Typography>
            ) : (
              <Typography sx={{fontSize: 11, color: '#D1D5DB'}}>—</Typography>
            )}
          </Box>

          {/* Status / Action */}
          <Box sx={{width: 36, display: 'flex', justifyContent: 'center'}}>
            {row.running ? (
              <Tooltip title="Running — click to view job">
                <IconButton size="small" onClick={() => row.jobId && onJobClick(row.jobId)}>
                  <CircularProgress size={14} thickness={4} />
                </IconButton>
              </Tooltip>
            ) : row.estimated ? (
              <Tooltip title="Run eval">
                <IconButton
                  size="small"
                  onClick={() => onRunEval(promptName, row.modelId)}
                  sx={{color: '#9CA3AF', '&:hover': {color: '#3B82F6'}}}
                >
                  <PlayArrowIcon sx={{fontSize: 16}} />
                </IconButton>
              </Tooltip>
            ) : (
              <Chip label="actual" size="small" sx={{height: 16, fontSize: 9, bgcolor: '#F0FDF4', color: '#065F46'}} />
            )}
          </Box>
        </Box>
      ))}
    </Box>
  );
}
